/**
 * What the learner sees when something breaks, instead of a blank page. Error details are
 * never shown (they may contain internals); they go to the console and to Cloud Logging. A page
 * left open across a deploy reloads itself once instead (lib/staleBuild.ts).
 */
import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';
import { describeError, reportToServer } from '../api/clientLog.ts';
import { isStaleBuildError, reloadForNewBuild } from '../lib/staleBuild.ts';
import styles from './ErrorScreens.module.css';

function ErrorLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className={styles['error-screen']} role="alert">
      <h1>{title}</h1>
      {children}
    </main>
  );
}

function ReloadButton() {
  return (
    <button
      type="button"
      className="primary"
      onClick={() => {
        window.location.reload();
      }}
    >
      再読み込みする
    </button>
  );
}

/** Route-level error screen (render errors inside a page, unknown URLs). */
export function RouteErrorScreen() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const stale = isStaleBuildError(error);
  // Decided once per error: reload for a new build, or show the screen below.
  const [reloading] = useState(() => stale && reloadForNewBuild());
  useEffect(() => {
    if (notFound || reloading) return;
    console.error(error);
    reportToServer('error', describeError(error));
  }, [error, notFound, reloading]);
  if (reloading) return <PageLoading />;
  if (stale) {
    return (
      <ErrorLayout title="アプリが更新されました">
        <p>最新の画面を読み込めませんでした。再読み込みしてください。</p>
        <div className="actions">
          <ReloadButton />
        </div>
      </ErrorLayout>
    );
  }
  if (notFound) {
    return (
      <ErrorLayout title="ページが見つかりません">
        <p>URLが正しいか確認してください。</p>
        <Link to="/">コースの一覧へ</Link>
      </ErrorLayout>
    );
  }
  return (
    <ErrorLayout title="画面の表示中に問題が起きました">
      <p>
        書いたコードやこれまでの操作が原因ではありません。再読み込みしても直らない場合は、講師に知らせてください。
      </p>
      <div className="actions">
        <ReloadButton />
        <Link to="/">コースの一覧へ</Link>
      </div>
    </ErrorLayout>
  );
}

/** Last-resort boundary around the whole app (errors outside the router). */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    if (isStaleBuildError(error) && reloadForNewBuild()) return;
    console.error(error, info.componentStack);
    reportToServer('error', describeError(error));
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <ErrorLayout title="画面の表示中に問題が起きました">
        <p>再読み込みしても直らない場合は、講師に知らせてください。</p>
        <ReloadButton />
      </ErrorLayout>
    );
  }
}

export function PageLoading() {
  return <main className="page-message muted">読み込み中…</main>;
}
